import { Build as BuildIcon } from "@mui/icons-material";
import { Divider, Grid, Typography } from "@mui/material";
import { unwrapResult } from "@reduxjs/toolkit";
import { AsolviSwitch } from "components/AsolviSwitch";
import { InvoiceAutocomplete } from "components/job/InvoiceAutocomplete";
import TimeBlockComponent from "components/job/times/TimeBlockComponent";
import { TotalTimeComponent } from "components/job/times/TotalTimeComponent";
import PrimaryButton from "components/PrimaryButton";
import { PromptDialog } from "components/PromptDialog";
import SecondaryGreyButton from "components/SecondaryGreyButton";
import { formatTimeDiff, isAbortError, isEmpty, mergeDateTime, toDateString } from "helpers";
import technicianSettings from "helpers/technicianSettings";
import { cloneDeep } from "lodash";
import { Times } from "models/travelTimes";
import { JobStatus } from "operations/schema/schema";
import { FC, useCallback, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useAppDispatch, useAppSelector } from "store";
import {
  acceptJob,
  addWorkTime,
  pauseTechStatus,
  refreshJob,
  selectSelectedJob,
  selectSelectedJobVisit,
  setAutoEndTime,
  setVisitValue,
  setWorkTimesInvoiceType,
  startVisitWork,
} from "store/slices/jobs.store";
import { addSnackbarMessage } from "store/slices/snackbar.store";
import { isFlagEnabled } from "store/slices/user.store";

export const WorkTab: FC = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const jobVisit = useAppSelector(selectSelectedJobVisit);
  const { autoEndTime, workTimes, workTimesInvoiceType } = jobVisit;
  const job = useAppSelector(selectSelectedJob);
  const [openDialog, setOpenDialog] = useState(false);
  const [index, setIndex] = useState(0);
  const hasUpdatedServiceJobEndpoint = useAppSelector((s) =>
    isFlagEnabled(s, "UpdatedServiceJobEndpoint"),
  );
  const allowChangeInvoiceType =
    technicianSettings.allowChangeInvoiceType && hasUpdatedServiceJobEndpoint;

  const isLastOneEnded =
    !autoEndTime &&
    workTimes[workTimes.length - 1].stopDate !== null &&
    workTimes[workTimes.length - 1].stopTime !== null;

  const dispatchUpdate = useCallback(
    async (times: Times[]) => {
      dispatch(setVisitValue({ key: "workTimes", value: [...times] }));
    },
    [dispatch],
  );

  const startWork = useCallback(async () => {
    await dispatch(addWorkTime());
    if (isEmpty(workTimes)) {
      if (job.status !== JobStatus.Accepted) {
        dispatch(
          acceptJob({
            jobId: job.id,
          }),
        )
          .then(unwrapResult)
          .then(() => {
            dispatch(refreshJob());
          });
      }
    }
    dispatch(startVisitWork({ jobId: job.id }))
      .then(unwrapResult)
      .then(({ queued }) => {
        if (queued) {
          dispatch(addSnackbarMessage({ key: "StartVisitWork-stored" }));
        } else {
          dispatch(addSnackbarMessage({ key: "StartVisitWork-success" }));
        }
      })
      .catch((e) => {
        if (isAbortError(e)) return;
        dispatch(addSnackbarMessage({ key: "StartVisitWork-fail" }));
      });
  }, [dispatch, job.id, job.status, workTimes]);

  const stopWork = useCallback(() => {
    let times = cloneDeep(workTimes);

    let last = times.pop();
    if (last) {
      last.stopDate = toDateString(Date.now());
      last.stopTime = toDateString(Date.now());
      dispatchUpdate([...times, last]);
      dispatch(pauseTechStatus({ jobId: job.id }));
    }
  }, [dispatch, dispatchUpdate, job.id, workTimes]);

  const deleteWorkTime = useCallback(
    (index: number) => {
      const newArray = workTimes.filter((time) => time !== workTimes[index]);
      dispatchUpdate(newArray);
    },
    [dispatchUpdate, workTimes],
  );

  const toggleAutoEndTime = useCallback(() => {
    if (autoEndTime && !workTimes[workTimes.length - 1].stopTime) {
      stopWork();
    }
    dispatch(setAutoEndTime({ autoEndTime: !autoEndTime }));
  }, [autoEndTime, dispatch, stopWork, workTimes]);

  const timeBlockOnChange = useCallback(
    async (
      id: number,
      fieldname: "startDate" | "startTime" | "stopDate" | "stopTime",
      value: Date,
    ) => {
      let newTimes = cloneDeep(workTimes);
      newTimes[id][fieldname] = toDateString(value);
      dispatchUpdate(newTimes);
    },
    [dispatchUpdate, workTimes],
  );

  const timeInvoiceOnChange = useCallback(
    async (id: number, value: string) => {
      let newTimes = cloneDeep(workTimes);
      newTimes[id]["invoiceType"] = value;
      dispatchUpdate(newTimes);
    },
    [dispatchUpdate, workTimes],
  );

  useEffect(() => {
    window.scrollTo({
      top: document.documentElement.scrollHeight,
      behavior: "smooth",
    });
  }, [autoEndTime]);

  return (
    <>
      <Grid container direction="column" spacing={1} width="100%" pl={1} pr={1}>
        {workTimes.map((times: Times, index: number) => {
          return (
            <>
              {index > 0 && <Divider variant="middle" component="div" />}
              <Grid key={`work-${index}`} item marginBottom={1}>
                <TimeBlockComponent
                  jobVisit={jobVisit}
                  setOpenDeleteDialog={setOpenDialog}
                  setIndex={setIndex}
                  timeBlockOnChange={timeBlockOnChange}
                  id={index}
                  isDeleteDisabled={workTimes.length === 1}
                  showLastOne={!(!isLastOneEnded && workTimes.length - 1 === index)}
                  timesLabel="workTimes"
                  timesText={intl.formatMessage({ id: "times.workTime" })}
                  disabled={
                    times.uploaded || times.pendingUpload || !technicianSettings.allowEditTimes
                  }
                  totalTime={
                    times.stopTime && times.stopDate
                      ? formatTimeDiff(
                          intl,
                          Math.floor(
                            (mergeDateTime(times.stopDate, times.stopTime).getTime() -
                              mergeDateTime(times.startDate, times.startTime).getTime()) /
                              1000 /
                              60,
                          ),
                        )
                      : ""
                  }
                />
              </Grid>
              {allowChangeInvoiceType && (
                <Grid item>
                  <InvoiceAutocomplete
                    onChange={(value: string) => {
                      timeInvoiceOnChange(index, value);
                    }}
                    value={times.invoiceType ?? null}
                  />
                </Grid>
              )}
            </>
          );
        })}
        {workTimes.length > 0 && (
          <>
            <Grid
              item
              container
              alignItems="center"
              justifyContent="space-between"
              direction="row"
              spacing={1}
              wrap="nowrap"
              sx={{
                mt: 1,
                ml: 0,
                pl: "0 !important",
              }}
            >
              <Grid item ml={1}>
                <Typography variant="body1" onClick={toggleAutoEndTime}>
                  <FormattedMessage id="times.setEndToCurrent" />
                </Typography>
              </Grid>
              <Grid item mr={1}>
                <AsolviSwitch checked={autoEndTime} onChange={toggleAutoEndTime} />
              </Grid>
            </Grid>
            <Grid item xs={12} ml={1}>
              <TotalTimeComponent times={workTimes} />
            </Grid>
          </>
        )}
        <Grid item>
          {isLastOneEnded || !workTimes[0]?.startTime ? (
            <>
              <PrimaryButton
                fullWidth
                startIcon={<BuildIcon />}
                onClick={() => {
                  startWork();
                  dispatch(setAutoEndTime({ autoEndTime: true }));
                }}
              >
                {intl.formatMessage({ id: "visit.startWork" })}
              </PrimaryButton>
              {allowChangeInvoiceType && (
                <Grid item>
                  <InvoiceAutocomplete
                    onChange={(value: string) => {
                      dispatch(setWorkTimesInvoiceType({ invoiceType: value }));
                    }}
                    value={workTimesInvoiceType ?? null}
                  />
                </Grid>
              )}
            </>
          ) : (
            <>
              <SecondaryGreyButton
                fullWidth
                onClick={() => {
                  deleteWorkTime(workTimes.length - 1);
                  dispatch(setAutoEndTime({ autoEndTime: false }));
                }}
                disabled={workTimes.length <= 1}
              >
                {intl.formatMessage({ id: "general.cancel" })}
              </SecondaryGreyButton>
              <PrimaryButton
                startIcon={<BuildIcon />}
                onClick={() => {
                  stopWork();
                  dispatch(setAutoEndTime({ autoEndTime: false }));
                }}
              >
                {intl.formatMessage({ id: "visit.stopWork" })}
              </PrimaryButton>
            </>
          )}
        </Grid>
      </Grid>
      <PromptDialog
        open={openDialog}
        setOpen={setOpenDialog}
        onOk={() => {
          deleteWorkTime(index);
        }}
        promptContent={<FormattedMessage id="times.deletePrompt" />}
        title={intl.formatMessage({ id: "visit.deleteTimesPrompt" })}
        okText={intl.formatMessage({ id: "general.continue" })}
      />
    </>
  );
};
